import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Switch } from "@headlessui/react";

import {
  DeliveryMethods,
  NotificationSettings as NotificationSettingsMap,
  NotificationSettingsType,
} from "@/api/rest/models/notification";
import { fetchNotificationSettings, updateNotificationSettings } from "@/api/rest/services/notificationService";
import { Banner } from "@/components/common/Banner";
import { useDebugLifecycle } from "@/utils/debug/useDebugLifecycle";

import Spinner from "../common/Spinner";

export function NotificationSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Set up local state to track form changes before submission
  const [localSettings, setLocalSettings] = useState<NotificationSettingsMap | null>(null);
  const [saveMessage, setSaveMessage] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  // Fetch notification settings using React Query
  const {
    data: settings,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ["notifications", "settings"],
    queryFn: async () => {
      const response = await fetchNotificationSettings();
      if (response.success && response.result) {
        // Initialize local state with fetched settings
        setLocalSettings(response.result);
        return response.result;
      }
      throw new Error(response.message || "Failed to load notification settings");
    },
    // Don't refetch on window focus to avoid overwriting user's unsaved changes
    refetchOnWindowFocus: false,
    // staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Set up mutation for saving settings
  const { mutate: saveSettings } = useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: (response) => {
      if (response.success) {
        // Update the cache with new settings from server
        if (response.result) {
          queryClient.setQueryData(["notifications", "settings"], response.result);
          setLocalSettings(response.result);
        }
        setSaveMessage({
          message: t("account.notificationSettingsSaved"),
          success: true,
        });
      } else {
        setSaveMessage({
          message: response.message || t("account.notificationSettingsError"),
          success: false,
        });
      }
    },
    onError: (error) => {
      setSaveMessage({
        message: error instanceof Error ? error.message : t("account.notificationSettingsError"),
        success: false,
      });
    },
  });

  useDebugLifecycle("NotificationSettings", {
    data: {
      url: window.location.href,
      queryState: { isLoading, isError, hasData: Boolean(settings) },
    },
  });

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  // Show error state
  if (isError || !localSettings) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-md">
        {error instanceof Error ? error.message : t("account.failedToLoadSettings")}
      </div>
    );
  }

  const toggleDeliveryMethod = (notificationType: NotificationSettingsType, method: keyof DeliveryMethods) => {
    if (!localSettings) {
      return;
    }

    const current = localSettings[notificationType];
    const currentValue = Boolean(current[method]);
    const updated: NotificationSettingsMap = {
      ...localSettings,
      [notificationType]: {
        ...current,
        [method]: !currentValue,
      },
    };

    // setSaveMessage(null);
    setLocalSettings(updated);
    saveSettings(updated);
  };

  return (
    <div className="flex flex-col gap-4">
      <NotificationTypeSection
        emailChecked={Boolean(localSettings.MAINTENANCE_REMINDERS.email)}
        mobileChecked={Boolean(localSettings.MAINTENANCE_REMINDERS.mobileApp)}
        onEmailChange={() => toggleDeliveryMethod("MAINTENANCE_REMINDERS", "email")}
        onMobileChange={() => toggleDeliveryMethod("MAINTENANCE_REMINDERS", "mobileApp")}
        titleTranslationKey="account.maintenanceReminders"
        subtitleTranslationKey="account.maintenanceRemindersDesc"
      />

      <NotificationTypeSection
        emailChecked={Boolean(localSettings.WARRANTY_MESSAGES.email)}
        mobileChecked={Boolean(localSettings.WARRANTY_MESSAGES.mobileApp)}
        onEmailChange={() => toggleDeliveryMethod("WARRANTY_MESSAGES", "email")}
        onMobileChange={() => toggleDeliveryMethod("WARRANTY_MESSAGES", "mobileApp")}
        titleTranslationKey="account.warrantyMessages"
        subtitleTranslationKey="account.warrantyMessagesDesc"
      />

      {saveMessage && (
        <div className="mt-3">
          <Banner message={saveMessage.message} success={saveMessage.success} />
        </div>
      )}
    </div>
  );
}

type NotificationTypeSectionProps = {
  emailChecked: boolean;
  mobileChecked: boolean;
  onEmailChange: () => void;
  onMobileChange: () => void;
  titleTranslationKey: string;
  subtitleTranslationKey: string;
  disabled?: boolean;
};

function NotificationTypeSection({
  emailChecked,
  mobileChecked,
  onEmailChange,
  onMobileChange,
  titleTranslationKey,
  subtitleTranslationKey,
}: NotificationTypeSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-between items-center ml-2">
        <div className="flex flex-col justify-between">
          <p className="font-medium">{t(titleTranslationKey)}</p>
          <p className="text-sm text-gray-500">{t(subtitleTranslationKey)}</p>
        </div>
      </div>
      <div className="flex flex-row justify-between items-center ml-8 mr-4">
        <p className="text-sm text-gray-500">{t("account.email")}</p>
        <span className="flex-grow" />
        <Switch
          checked={emailChecked}
          onChange={onEmailChange}
          className="group inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition data-[checked]:bg-dobu-orange"
        >
          <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
        </Switch>
      </div>
      <div className="flex flex-row justify-between items-center ml-8 mr-4">
        <p className="text-sm text-gray-500">{t("account.mobileApp")}</p>
        <span className="flex-grow" />
        <Switch
          checked={mobileChecked}
          onChange={onMobileChange}
          className="group inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition data-[checked]:bg-dobu-orange"
        >
          <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
        </Switch>
      </div>
    </div>
  );
}
